import { Logger } from '@calendly/logging-service'
// @ts-ignore-next-line
import { groupBy, sortBy } from 'lodash-es'
import moment from 'moment-timezone'

/**
 * Date group shape produced by groupAndMap and consumed by combineMeetings.
 * Matches features/meetings/hooks/search_data legacy format.
 */
export interface DateGroup {
  rawDate: string
  date: string
  events: Array<{ uuid: string; startTime: string; [key: string]: unknown }>
}

/** GraphQL meeting edge: { node } with start_time. */
export interface MeetingEdge {
  node: {
    start_time: string
    [key: string]: unknown
  }
}

/** Mapper used to turn a node into a meeting (e.g. mapNodeToMeeting from features/meetings/utils). */
export type MapNodeToMeeting = (
  node: MeetingEdge['node'],
  publisherTZ: string,
  hasMsTeamsFlag?: boolean
) => DateGroup['events'][number]

const getDuplicatesAndUniques = (
  list: DateGroup['events'],
  key: string
): { duplicates: DateGroup['events']; uniqueList: DateGroup['events'] } => {
  const groups = groupBy(list, key)
  const duplicates = Object.values(groups).flatMap((group) => (group.length > 1 ? [group[0]] : [])
  )
  const uniqueList = Object.values(groups).map((group) => group[0])
  return { duplicates, uniqueList }
}

/**
 * Combines current and new date-grouped meetings, deduplicating by uuid and sorting.
 * Legacy API: (currentMeetings, newMeetings, currentUserUuid).
 */
export function combineMeetings(
  currentMeetings: DateGroup[] | DateGroup,
  newMeetings: DateGroup[] | DateGroup,
  currentUserUuid: string | undefined
): DateGroup[] {
  const currentMeetingsArray = Array.isArray(currentMeetings)
    ? currentMeetings
    : [currentMeetings]
  const newMeetingsArray = Array.isArray(newMeetings) ? newMeetings : [newMeetings]

  const combinedMap: Record<string, DateGroup> = currentMeetingsArray.reduce(
    (acc, val) => {
      acc[val.rawDate] = val
      return acc
    },
    {} as Record<string, DateGroup>
  )

  for (const meeting of newMeetingsArray) {
    const slot = combinedMap[meeting.rawDate]
    if (slot) {
      const { duplicates, uniqueList } = getDuplicatesAndUniques(
        [...slot.events, ...meeting.events],
        'uuid'
      )
      if (duplicates.length > 0) {
        Logger.warn('Duplicate search api meetings found', {
          current_user: currentUserUuid,
          uuids: duplicates.map((event) => event.uuid),
        })
      }
      slot.events = uniqueList
    } else {
      combinedMap[meeting.rawDate] = { ...meeting }
    }
  }

  Object.keys(combinedMap).forEach((key) => {
    const sortedEvents = sortBy(
      combinedMap[key].events,
      (event) => moment(event.startTime).valueOf()
    )
    const { duplicates, uniqueList } = getDuplicatesAndUniques(sortedEvents, 'uuid')
    if (duplicates.length > 0) {
      Logger.warn('Duplicate search api meetings found', {
        current_user: currentUserUuid,
        uuids: duplicates.map((event) => event.uuid),
      })
    }
    combinedMap[key].events = uniqueList
  })

  return sortBy(Object.values(combinedMap), (item) => new Date(item.date))
}

/**
 * Groups meeting edges by date (in publisher TZ) and maps each node to a meeting via the provided mapper.
 * Legacy API: (meetings, publisherTZ, hasMsTeamsFlag); mapper is injected so this module stays free of features/meetings imports.
 */
export function groupAndMap(
  meetings: MeetingEdge[],
  publisherTZ: string,
  hasMsTeamsFlag: boolean = false,
  mapNodeToMeeting: MapNodeToMeeting
): DateGroup[] {
  const grouped: Record<string, DateGroup> = {}

  meetings.forEach((meeting) => {
    const date = moment(meeting.node.start_time).tz(publisherTZ)
    const dateStr = date.format('YYYY-MM-DD')
    if (!grouped[dateStr]) {
      grouped[dateStr] = {
        rawDate: dateStr,
        date: date.format('dddd, MMMM D, YYYY'),
        events: [],
      }
    }
    grouped[dateStr].events.push(
      mapNodeToMeeting(meeting.node, publisherTZ, hasMsTeamsFlag)
    )
  })

  return Object.values(grouped)
}
